import React, { useState } from 'react';
import { 
  Button, 
  Stack, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton, 
  CircularProgress,
  Typography,
  Box,
  Divider
} from '@mui/material';
import axios from 'axios';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

export const API_BASE = '/api/v1/plans';

const GoogleORToolsCalendarPlanningCell: React.FC<{ plan: any }> = ({ plan }) => {
  const [open, setOpen] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [requestJson, setRequestJson] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [resultJson, setResultJson] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meta = plan.calendar_planning_meta || {};
  const ortoolsMeta = meta.google_ortools_calendar_solution || {};

  // Helper to fetch and show request JSON
  const handleShowRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/${plan.id}/google-ortools-calendar-planning/request`);
      setRequestJson(JSON.stringify(res.data, null, 2));
      setShowRequest(true);
    } catch (e: any) {
      setRequestJson('Failed to load request JSON');
      setError(e?.response?.data?.detail || 'Failed to load request JSON');
      setShowRequest(true);
    }
    setLoading(false);
  };

  // Helper to fetch and show result JSON
  const handleShowResult = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/${plan.id}/google-ortools-calendar-planning/solution`);
      setResultJson(JSON.stringify(res.data, null, 2));
      setShowResult(true);
    } catch (e: any) {
      setResultJson('Failed to load result');
      setError(e?.response?.data?.detail || 'Failed to load result');
      setShowResult(true);
    }
    setLoading(false);
  };

  // Button handlers
  const handleGenerateRequest = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/${plan.id}/google-ortools-calendar-planning/generate-request`);
      window.location.reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to generate request');
    }
    setLoading(false);
  };

  const handleSolve = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE}/${plan.id}/google-ortools-calendar-planning/solve`);
      console.log('Google OR-Tools Calendar Planning Response:', response.data);
      window.location.reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to solve calendar planning problem');
    }
    setLoading(false);
  };

  // Get status information
  const getStatusInfo = () => {
    const status = ortoolsMeta.status || 'not_started';
    const solvedAt = ortoolsMeta.solved_at;
    
    switch (status) {
      case 'completed':
        return {
          label: 'Completed',
          color: 'success' as const,
          description: solvedAt ? `Solved at ${new Date(solvedAt).toLocaleString()}` : 'Solution found'
        };
      case 'error':
        return {
          label: 'Error',
          color: 'error' as const,
          description: ortoolsMeta.error || 'An error occurred'
        };
      case 'not_started':
      default:
        return {
          label: 'Not Started',
          color: 'default' as const,
          description: 'Calendar planning not yet performed'
        };
    }
  };

  const statusInfo = getStatusInfo();

  // UI logic for modal content
  let modalContent: React.ReactNode = null;
  
  if (!ortoolsMeta.status || ortoolsMeta.status === 'not_started') {
    // No solution yet
    modalContent = (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Google OR-Tools Calendar Planning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate a calendar planning request and solve it using Google OR-Tools CP-SAT solver.
          </Typography>
        </Box>
        
        <Stack spacing={2}>
          <Button 
            variant="contained" 
            size="medium" 
            onClick={handleGenerateRequest} 
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={20} /> : 'Generate Request'}
          </Button>
          
          <Button 
            variant="outlined" 
            size="medium" 
            onClick={handleSolve} 
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={20} /> : 'Generate & Solve'}
          </Button>
        </Stack>
        
        {error && (
          <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}
      </Stack>
    );
  } else {
    // Solution exists
    modalContent = (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Google OR-Tools Calendar Planning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Calendar planning solution using Google OR-Tools CP-SAT solver.
          </Typography>
        </Box>
        
        <Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip 
              label={statusInfo.label} 
              color={statusInfo.color} 
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              {statusInfo.description}
            </Typography>
          </Stack>
        </Box>
        
        <Divider />
        
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleShowRequest} 
              disabled={loading}
            >
              Show Request
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleShowResult} 
              disabled={loading}
            >
              Show Solution
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleSolve} 
              disabled={loading}
            >
              Re-solve
            </Button>
          </Stack>
          
          {ortoolsMeta.metadata && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Solution Metadata:</Typography>
              <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem' }}>
                {JSON.stringify(ortoolsMeta.metadata, null, 2)}
              </Typography>
            </Box>
          )}
        </Stack>
        
        {error && (
          <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}
        
        {/* Request Dialog */}
        <Dialog open={showRequest} onClose={() => setShowRequest(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Google OR-Tools Calendar Planning Request</DialogTitle>
          <DialogContent>
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-all',
              fontSize: '0.8rem',
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px'
            }}>
              {requestJson}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRequest(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        
        {/* Result Dialog */}
        <Dialog open={showResult} onClose={() => setShowResult(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Google OR-Tools Calendar Planning Solution</DialogTitle>
          <DialogContent>
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-all',
              fontSize: '0.8rem',
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px'
            }}>
              {resultJson}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResult(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }

  return (
    <>
      <IconButton 
        onClick={() => setOpen(true)} 
        size="small" 
        title="Google OR-Tools Calendar Planning"
        color={statusInfo.color === 'success' ? 'success' : 'default'}
      >
        <CalendarTodayIcon />
      </IconButton>
      
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Google OR-Tools Calendar Planning: {plan.name}
        </DialogTitle>
        <DialogContent>
          {modalContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoogleORToolsCalendarPlanningCell; 